import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ProgrammeForm, ProgrammeFormValues, mapProgrammeFormValuesToPayload } from '@/components/app/programmes/ProgrammeForm'
import { programmesService } from '@/services/programmesService'
import { departmentsService } from '@/services/departmentsService'
import { Department } from '@/models/department.model'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/utils/cn'
import { paths } from '@/routes/path'
import BackButton from '@/components/ui/BackButton'
import { ProgrammeJSON, ProgrammeModel } from '@/models/programme.model'

export const ProgrammeCreatePage: React.FC = () => {
  const navigate = useNavigate()
  const [departments, setDepartments] = useState<Department[]>([])
  const [programme, setProgramme] = useState<ProgrammeJSON | null>(null)
  const [loadingDepartments, setLoadingDepartments] = useState<boolean>(true)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadDepartments = async () => {
      setLoadingDepartments(true)
      try {
        const response = await departmentsService.getDepartmentsPage({ page: 1, perPage: 100 })
        setDepartments(response.data)
      } catch (err) {
        setError((err as Error).message || 'Unable to load departments.')
      } finally {
        setLoadingDepartments(false)
      }
    }

    loadDepartments()
  }, [])

  const handleSubmit = async (values: ProgrammeFormValues) => {
    setSubmitting(true)
    setError(null)
    try {
      const payload = mapProgrammeFormValuesToPayload(values);
      setProgramme(payload)
      await programmesService.createProgramme(payload)
      navigate(paths.app.programmes.root.absolute)
    } catch (err) {
      setError((err as Error).message || 'Unable to save programme.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loadingDepartments) {
    return <div className="flex min-h-[50vh] items-center justify-center text-secondary-500">Loading...</div>
  }

  if (departments.length === 0) {
    return (
      <Card className="mx-auto max-w-2xl">
        <CardContent className="space-y-4 p-6 text-center">
          <h1 className="text-2xl font-semibold text-secondary-900">No departments available</h1>
          <p className="text-secondary-600">Create a department before adding programmes.</p>
          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={() => navigate(paths.app.departments.root.absolute)}>
              Manage Departments
            </Button>
            <Button onClick={() => navigate(paths.app.programmes.root.absolute)}>Back to Programmes</Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex items-center space-x-2">
          <BackButton/>
          <h1 className="text-3xl font-bold text-secondary-900">Create Programme</h1>
        </div>
        <p className="text-secondary-600">Add a new programme with payment details, segments, and admission requirements.</p>
      </header>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <Card className='p-5'>
        <ProgrammeForm
          mode="create"
          programme={programme?ProgrammeModel.fromJSON(programme):null}
          departments={departments.map((department) => ({ id: department.id, name: department.name }))}
          submitting={submitting}
          onSubmit={handleSubmit}
          onCancel={() => navigate(paths.app.programmes.root.absolute)}
        />
      </Card>
    </div>
  )
}

export default ProgrammeCreatePage
